import { useState, useEffect, FormEvent } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation } from 'convex/react';
import { api } from '../../convex/_generated/api';
import './ResetPassword.css';

export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Validate token
  const tokenValidation = useQuery(
    api.auth.validateResetToken,
    token ? { token } : "skip"
  );

  const resetPassword = useMutation(api.auth.resetPassword);

  useEffect(() => {
    if (!token) {
      setError('Missing reset token');
    }
  }, [token]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');

    // Validation
    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (!token) {
      setError('Invalid reset token');
      return;
    }

    setIsLoading(true);

    try {
      await resetPassword({ token, newPassword });
      setSuccess(true);
      // Redirect to login after 3 seconds
      setTimeout(() => {
        navigate('/');
      }, 3000);
    } catch (err: any) {
      // Clean up technical error details
      const errorMessage = err.message || 'Failed to reset password';
      const cleanMessage = errorMessage
        .split('\n')[0]
        .replace(/\[CONVEX.*?\]/g, '')
        .replace(/at handler.*$/g, '')
        .replace(/Called by client.*$/g, '')
        .replace(/Uncaught Error:\s*/g, '')
        .replace(/Server Error\s*/g, '')
        .trim();

      setError(cleanMessage || 'Failed to reset password');
    } finally {
      setIsLoading(false);
    }
  };

  // Loading state
  if (!token || tokenValidation === undefined) {
    return (
      <div className="reset-password-container">
        <div className="reset-password-card">
          <div className="loading">Validating reset link...</div>
        </div>
      </div>
    );
  }

  // Invalid or expired token
  if (tokenValidation && !tokenValidation.valid) {
    return (
      <div className="reset-password-container">
        <div className="reset-password-card">
          <div className="error-icon">⚠️</div>
          <h2>Invalid or Expired Link</h2>
          <p>
            {tokenValidation.reason === 'expired'
              ? 'This password reset link has expired. Please request a new one.'
              : 'This password reset link is invalid. Please request a new one.'}
          </p>
          <button
            className="btn-primary"
            onClick={() => navigate('/')}
          >
            Back to Login
          </button>
        </div>
      </div>
    );
  }

  // Success state
  if (success) {
    return (
      <div className="reset-password-container">
        <div className="reset-password-card">
          <div className="success-icon">✅</div>
          <h2>Password Reset Successfully</h2>
          <p>Your password has been updated. You can now log in with your new password.</p>
          <p className="redirect-text">Redirecting to login...</p>
        </div>
      </div>
    );
  }

  // Reset form
  return (
    <div className="reset-password-container">
      <div className="reset-password-card">
        <div className="reset-password-header">
          <h2>🔒 Set New Password</h2>
          <p>
            Resetting password for <strong>{tokenValidation?.email}</strong>
          </p>
        </div>

        <form onSubmit={handleSubmit} className="reset-password-form">
          {error && (
            <div className="reset-password-error">
              ⚠️ {error}
            </div>
          )}

          <div className="form-group">
            <label htmlFor="new-password">New Password</label>
            <input
              id="new-password"
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              required
              minLength={8}
              placeholder="At least 8 characters"
              disabled={isLoading}
              autoFocus
            />
            <span className="field-hint">Minimum 8 characters</span>
          </div>

          <div className="form-group">
            <label htmlFor="confirm-password">Confirm Password</label>
            <input
              id="confirm-password"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              minLength={8}
              placeholder="Re-enter your password"
              disabled={isLoading}
            />
          </div>

          <button
            type="submit"
            className="btn-submit"
            disabled={isLoading}
          >
            {isLoading ? 'Resetting...' : 'Reset Password'}
          </button>

          <button
            type="button"
            className="btn-back"
            onClick={() => navigate('/')}
            disabled={isLoading}
          >
            ← Back to Login
          </button>
        </form>
      </div>
    </div>
  );
}
